import {
  Autocomplete,
  FormControl,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Date from "./Date";
import { GenderPicker } from "./GenderPicker";
import { BG_COLOR } from "../../enums/Colors";
import { ThemeContext } from "../../contexts/ThemeContext";
import usePrimaryColor from "../../hooks/usePrimaryColor";
import catBreeds from "../../features/master/species/breeds/CatBreeds.json";
import dogBreeds from "../../features/master/species/breeds/DogBreeds.json";
import { mandatory } from "../../utils/Functions";
import CharCountField from "./CharCountField";
import useUser from "../../hooks/useUser";
import { BloodDonorSwitch } from "./BloodDonorSwitch";

export default function NewPetBody({
  descKey = "description",
  aggressionKey = "aggression",
  genderKey = "gender",
  speciesKey = "type",
  nameKey = "name",
  dateKey = "birthdate",
  bloodDonorKey = "bloodDonor",
  bloodTypeKey = "bloodType",
  species,
  breedKey = "breed",
  testIdPrefix,
  mt = 0
}) {
  const { mode } = React.useContext(ThemeContext);
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const color = usePrimaryColor();
  const genderPickerBgColor = mode == "dark" ? BG_COLOR : "white";
  const { t } = useTranslation();
  const { roles } = useUser();
  const [gender, setGender] = React.useState(true);
  const [selectedBreed, setSelectedBreed] = useState([]);
  let parsedDogBreeds = [];
  let parsedCatBreeds = [];
  dogBreeds.dog_breeds.forEach((breed) => parsedDogBreeds.push(breed));
  parsedDogBreeds.sort((a, b) => t(a).localeCompare(t(b)));
  catBreeds.cat_breeds.forEach((breed) => parsedCatBreeds.push(breed));
  parsedCatBreeds.sort((a, b) => t(a).localeCompare(t(b)));

  function getBreedList(newBreed) {
    if (newBreed?.breed === "catBreeds") {
      setSelectedBreed(parsedCatBreeds);
    } else if (newBreed?.breed === "dogBreeds") {
      setSelectedBreed(parsedDogBreeds);
    } else {
      setSelectedBreed([]);
    }
  }

  function removeBreed() {
    setValue(breedKey, null);
  }

  function handleGender() {
    setGender((prevVal) => !prevVal);
    if (gender) {
      setValue(genderKey, "FEMALE");
    } else {
      setValue(genderKey, "MALE");
    }
  }

  return (
    <Grid container spacing={2} mt={mt}>
      <Grid item xs={12}>
        <Controller
          name={nameKey}
          control={control}
          rules={{ required: t("EmptyField") }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              fullWidth
              inputProps={{
                maxLength: 120,
                ...(testIdPrefix && {
                  "data-testid": `${testIdPrefix}.name-input`,
                }),
              }}
              label={mandatory(t("Name"))}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>

      {/* Pet Type Selection */}
      <Grid item xs={12} sm={12} md={6}>
        <Controller
          name={speciesKey}
          control={control}
          rules={{ required: t("EmptyField") }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <Autocomplete
                options={species}
                value={field.value || null}
                getOptionLabel={(option) =>
                  option?.title ? t(option.title) : ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                onChange={(event, value) => {
                  field.onChange(value || null);
                  getBreedList(value);
                  removeBreed();
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={mandatory(t("Species"))}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      ...params.inputProps,
                      ...(testIdPrefix && {
                        "data-testid": `${testIdPrefix}.species-select`,
                      }),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>{t(option.title)}</li>
                )}
                fullWidth
                clearText={t("Clear")}
                closeText={t("Close")}
                openText={t("Open")}
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} lg={6} md={6}>
        <Controller
          name={breedKey}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <Autocomplete
                options={selectedBreed}
                noOptionsText={t("noSelectableBreed")}
                value={field.value ?? null}
                getOptionLabel={(option) => (option ? t(option) : "")}
                onChange={(event, value) => field.onChange(value || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("breed")}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      ...params.inputProps,
                      ...(testIdPrefix && {
                        "data-testid": `${testIdPrefix}.breed-select`,
                      }),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>{t(option)}</li>
                )}
                fullWidth
                clearText={t("Clear")}
                closeText={t("Close")}
                openText={t("Open")}
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Stack
          border={gender ? `1px solid ${color}` : "1px solid hotpink"}
          p={1}
          borderRadius={"5px"}
          direction={"row"}
          sx={{ width: "100%", height: "100%" }}
          justifyContent={"space-between"}
          alignItems={"center"}
          bgcolor={genderPickerBgColor}
        >
          <Typography color={gender ? color : "hotpink"}>
            {t("Gender")}
          </Typography>
          <GenderPicker
            onClick={(e) => handleGender(e)}
            inputProps={
              testIdPrefix
                ? { "data-testid": `${testIdPrefix}.gender-switch` }
                : undefined
            }
          />
        </Stack>
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <Date
          name={dateKey}
          rulesValue={{ required: false }}
          control={control}
          disableFuture={true}
          errors={errors}
          label={t("PetDobOptional")}
          openTo="year"
          testId={testIdPrefix && `${testIdPrefix}.date-input`}
        />
      </Grid>
      <Grid item xs={12} lg={6} md={6}>
        <FormControl fullWidth>
          <TextField
            inputProps={{
              maxLength: 30,
              ...(testIdPrefix && {
                "data-testid": `${testIdPrefix}.passport-input`,
              }),
            }}
            key="passport"
            label={t("Passport")}
            {...register("passport")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6} md={6}>
        <FormControl fullWidth>
          <TextField
            inputProps={{
              maxLength: 30,
              ...(testIdPrefix && {
                "data-testid": `${testIdPrefix}.microchip-input`,
              }),
            }}
            key="microchip"
            label={t("Microchip")}
            {...register("microchip")}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <BloodDonorSwitch
          bloodDonorKey={bloodDonorKey}
          bloodTypeKey={bloodTypeKey}
          testIdPrefix={testIdPrefix}
        />
      </Grid>

      {!roles.user && (
        <Grid item xs={12} lg={12} md={12}>
          <CharCountField
            name={aggressionKey}
            label={t("Aggression")}
            maxChar={255}
            maxSize={1}
            testId={testIdPrefix && `${testIdPrefix}.aggression-input`}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <CharCountField
          register={register}
          name={descKey}
          label={t("Description")}
          fullWidth
          testId={testIdPrefix && `${testIdPrefix}.description-input`}
        />
      </Grid>
    </Grid>
  );
}
